"use client";

import { IconButton } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import useVoiceRecorder from "@/features/audio-input/hooks/useVoiceRecorder";
import { useTranscribeAudioMutation } from "@/features/origins";
import type { SourcedWhere } from "@/generated/fastapi";
import { showErrorToast, showInfoToast } from "@/shared/feedback";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import usePageSearch from "../hooks/usePageSearch";

function resolveVoiceSearchSource(
  pageViewType: ReturnType<typeof usePageSearch>["pageViewType"],
): SourcedWhere {
  return pageViewType === "backlog_extract" ? "backlog" : "studio";
}

export default function NavbarVoiceSearchAction({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const { pageSearch, pageViewType } = usePageSearch();
  const {
    error,
    isSupported,
    notice,
    recording,
    resetRecording,
    startRecording,
    status,
    stopRecording,
  } = useVoiceRecorder();
  const transcribeAudioMutation = useTranscribeAudioMutation();
  const processedRecordingRef = useRef<string | null>(null);

  useEffect(() => {
    if (!error) {
      return;
    }

    showErrorToast(error);
  }, [error]);

  useEffect(() => {
    if (!notice) {
      return;
    }

    showInfoToast(notice);
  }, [notice]);

  useEffect(() => {
    if (!recording || status !== "recorded") {
      return;
    }

    if (processedRecordingRef.current === recording.objectUrl) {
      return;
    }

    processedRecordingRef.current = recording.objectUrl;

    void (async () => {
      try {
        const result = await transcribeAudioMutation.mutateAsync({
          selectedFile: recording.file,
          sourcedWhere: resolveVoiceSearchSource(pageViewType),
        });
        const transcript = result.transcribed_text?.trim() ?? "";

        if (!transcript) {
          showErrorToast("No speech was detected in the recording.");
          return;
        }

        pageSearch.setSearchQuery(transcript);
      } catch (transcriptionError) {
        const message =
          transcriptionError instanceof Error
            ? transcriptionError.message
            : "Failed to transcribe audio";
        showErrorToast(message);
      } finally {
        resetRecording();
        processedRecordingRef.current = null;
      }
    })();
  }, [
    pageSearch,
    pageViewType,
    recording,
    resetRecording,
    status,
    transcribeAudioMutation,
  ]);

  const isRecording = status === "recording";
  const isTranscribing = transcribeAudioMutation.isPending;
  const icon = isTranscribing
    ? "transcribe"
    : isRecording
      ? "stop"
      : "microphone";
  const humanReadable = isTranscribing
    ? "Transcribing voice search"
    : isRecording
      ? "Stop voice search recording"
      : isSupported
        ? "Start voice search recording"
        : "Voice search unavailable";

  const handleClick = () => {
    if (isTranscribing || disabled) {
      return;
    }

    if (isRecording) {
      stopRecording();
      return;
    }

    void startRecording();
  };

  return (
    <IconButton
      aria-label={humanReadable}
      variant="ghost"
      size="xs"
      colorPalette={isRecording ? "red" : "gray"}
      color={isSupported ? undefined : "moveon.text.muted"}
      disabled={disabled || isTranscribing}
      onClick={handleClick}
    >
      <GenericIcon
        icon={icon}
        size="sm"
        humanReadable={humanReadable}
        loading={isTranscribing}
      />
    </IconButton>
  );
}
